import { useState, useEffect } from "react";

// ── Design tokens (from Home.jsx colour palette) ──────────────────────────────
const C = {
    primary: "#2c5363",
    primaryDark: "#1a3a47",
    green: "#16b287",
    greenDark: "#2c9e7a",
    heading: "#283646",
    headingAlt: "#37546D",
    bg: "#ffffff",
};

// ── Article data ──────────────────────────────────────────────────────────────
const articles = [
    {
        id: 1,
        thumb: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
        label: "BHARAT CONNECT",
        date: "June 30, 2025",
        title: "CSP Powers Over 9.5 Million Rural Bill Payments in 2025 Via Bharat Connect",
        url: "",
    },
    {
        id: 2,
        thumb: `linear-gradient(135deg, ${C.green}, ${C.greenDark})`,
        label: "WOMEN'S DAY",
        date: "March 8, 2025",
        title: "'Brave Naari Adhikari': CSP Highlights Rural Women's Financial Strength On Women's Day",
        url: "",
    },
    {
        id: 3,
        thumb: `linear-gradient(135deg, ${C.headingAlt}, ${C.heading})`,
        label: "BANKING ECOSYSTEM",
        date: "March 26, 2025",
        title: "Business Correspondents: Building a community-led banking ecosystem",
        url: "",
    },
    {
        id: 4,
        thumb: `linear-gradient(135deg, ${C.green}, ${C.primaryDark})`,
        label: "DIGITAL INDIA",
        date: "April 5, 2025",
        title: "CSP enables seamless digital transactions for millions of rural users",
        url: "",
    },
    {
        id: 5,
        thumb: `linear-gradient(135deg, ${C.primary}, ${C.green})`,
        label: "RECORD GTV",
        date: "Sep 2024",
        title: "CSP Hits Record Rs 11,485 Cr GTV In March 2025",
        url: "",
    },
];

// ── Arrow icon ────────────────────────────────────────────────────────────────
const ArrowIcon = ({ size = 13, color = "currentColor" }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        style={{ flexShrink: 0, transition: "transform 0.2s" }}
    >
        <path
            d="M3 8h10M9 4l4 4-4 4"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ── Single card ───────────────────────────────────────────────────────────────
const MediaCard = ({ article }) => {
    const [hovered, setHovered] = useState(false);
    const [linkHovered, setLinkHovered] = useState(false);

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                flexShrink: 0,
                width: "clamp(220px, 68vw, 340px)",
                border: `1px solid ${C.primary}`,
                borderRadius: 14,
                overflow: "hidden",
                background: C.bg,
                boxShadow: hovered
                    ? "0 8px 26px rgba(44,83,99,0.18)"
                    : "0 2px 10px rgba(44,83,99,0.09)",
                display: "flex",
                flexDirection: "column",
                transition: "box-shadow 0.22s, transform 0.22s",
                transform: hovered ? "translateY(-3px)" : "translateY(0)",
                cursor: "default",
            }}
        >
            {/* Thumbnail */}
            <div
                style={{
                    width: "100%",
                    height: 155,
                    background: article.thumb,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 8,
                    fontWeight: 800,
                    color: "rgba(255,255,255,0.7)",
                    letterSpacing: "2.5px",
                    textTransform: "uppercase",
                    flexShrink: 0,
                }}
            >
                {article.label}
            </div>

            {/* Body */}
            <div
                style={{
                    padding: "12px 14px 14px",
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                }}
            >
                {/* Date */}
                <div
                    style={{
                        fontSize: 9,
                        fontWeight: 700,
                        color: C.green,
                        letterSpacing: "1.2px",
                        textTransform: "uppercase",
                        marginBottom: 6,
                    }}
                >
                    {article.date}
                </div>

                {/* Title — 2-line clamp */}
                <div
                    style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: C.heading,
                        lineHeight: 1.45,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        marginBottom: 10,
                        flex: 1,
                    }}
                >
                    {article.title}
                </div>

                {/* Read More link */}
                <a
                    href={article.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={() => setLinkHovered(true)}
                    onMouseLeave={() => setLinkHovered(false)}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: linkHovered ? C.green : C.primary,
                        textDecoration: "none",
                        alignSelf: "flex-start",
                        transition: "color 0.18s",
                        WebkitTapHighlightColor: "transparent",
                    }}
                >
                    Read More
                    <ArrowIcon color={linkHovered ? C.green : C.primary} />
                </a>
            </div>
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────
const MediaCoverage = () => {
    const [btnHovered, setBtnHovered] = useState(false);
    const [trackPaused, setTrackPaused] = useState(false);

    // Inject keyframe animation into <head> once
    useEffect(() => {
        const id = "media-marquee-keyframes";
        if (!document.getElementById(id)) {
            const style = document.createElement("style");
            style.id = id;
            style.textContent = `
        @keyframes mediaCoverageMarquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `;
            document.head.appendChild(style);
        }
        return () => {
            const el = document.getElementById(id);
            if (el) el.remove();
        };
    }, []);

    // Duplicate articles for seamless infinite loop
    const loopedArticles = [...articles, ...articles];

    return (
        <section
            style={{
                padding: "40px 0 36px",
                background: C.bg,
                fontFamily: "'Segoe UI', sans-serif",
                WebkitFontSmoothing: "antialiased",
                overflow: "hidden",
            }}
        >
            {/* ── Header ── */}
            <div
                style={{
                    textAlign: "center",
                    padding: "0 16px",
                    marginBottom: 28,
                }}
            >
                {/* Label */}
                <div
                    style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "2.5px",
                        textTransform: "uppercase",
                        color: C.green,
                        marginBottom: 8,
                    }}
                >
                    Press &amp; News
                </div>

                {/* Title */}
                <h2
                    style={{
                        fontSize: "clamp(24px, 7vw, 36px)",
                        fontWeight: 800,
                        color: C.heading,
                        lineHeight: 1.15,
                        marginBottom: 10,
                        margin: "0 0 10px 0",
                    }}
                >
                    Media{" "}
                    <span
                        style={{
                            background: `linear-gradient(90deg, ${C.green}, ${C.greenDark})`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                        }}
                    >
                        Coverage
                    </span>
                </h2>

                {/* Subtitle */}
                <p
                    style={{
                        fontSize: "clamp(13px, 3.8vw, 15px)",
                        color: C.headingAlt,
                        fontWeight: 500,
                        lineHeight: 1.5,
                        maxWidth: 480,
                        margin: "0 auto",
                    }}
                >
                    Stay Updated With Our Latest News, Campaigns, and Insights
                </p>
            </div>

            {/* ── Marquee scroll container ── */}
            <div
                style={{
                    width: "100%",
                    overflow: "hidden",
                    position: "relative",
                }}
            >
                {/* Left fade */}
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: 0,
                        width: "clamp(30px, 5vw, 80px)",
                        background: `linear-gradient(to right, ${C.bg}, transparent)`,
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                />

                {/* Right fade */}
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        right: 0,
                        width: "clamp(30px, 5vw, 80px)",
                        background: `linear-gradient(to left, ${C.bg}, transparent)`,
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                />

                {/* Scrolling track */}
                <div
                    onMouseEnter={() => setTrackPaused(true)}
                    onMouseLeave={() => setTrackPaused(false)}
                    style={{
                        display: "flex",
                        gap: "clamp(12px, 2vw, 18px)",
                        padding: "8px 0 16px",
                        width: "max-content",
                        animation: "mediaCoverageMarquee 22s linear infinite",
                        animationPlayState: trackPaused ? "paused" : "running",
                        willChange: "transform",
                    }}
                >
                    {loopedArticles.map((article, index) => (
                        <MediaCard key={`${article.id}-${index}`} article={article} />
                    ))}
                </div>
            </div>

            {/* ── View All button ── */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "0 16px",
                    marginTop: 24,
                }}
            >
                <a
                    href=""
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={() => setBtnHovered(true)}
                    onMouseLeave={() => setBtnHovered(false)}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        background: btnHovered ? C.green : C.primary,
                        color: "#fff",
                        fontSize: "clamp(13px, 3.5vw, 14px)",
                        fontWeight: 700,
                        padding: "12px 26px",
                        borderRadius: 50,
                        textDecoration: "none",
                        transition: "background 0.2s, transform 0.18s",
                        transform: btnHovered ? "translateY(-2px)" : "translateY(0)",
                        whiteSpace: "nowrap",
                        WebkitTapHighlightColor: "transparent",
                    }}
                >
                    View all insights
                    <svg
                        width={16}
                        height={16}
                        viewBox="0 0 18 18"
                        fill="none"
                        style={{ flexShrink: 0 }}
                    >
                        <path
                            d="M4 9h10M10 5l4 4-4 4"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </a>
            </div>
        </section>
    );
};

export default MediaCoverage;